import type enUS from "./locales/en-US.json"
import { useNavigatorLanguage } from "@vueuse/core"
import merge from "lodash-es/merge"
import { createI18n } from "vue-i18n"
import { languageMetadata } from "./constants"
import { getDefaultLanguage } from "./utils"

const allLocaleModules = import.meta.glob("./locales/**/*.json", {
  eager: true,
}) as Record<string, { default: unknown }>

export function getMessagesAndLanguages(brand?: string) {
  const messages: Record<string, any> = {}
  const languages: string[] = []
  const defaultLocales: Record<string, any> = {}

  for (const path of Object.keys(allLocaleModules)) {
    const match = path.match(/^\.\/locales\/([a-z]{2}-[A-Z]{2})\.json$/)

    if (match) {
      const langCode = match[1]

      defaultLocales[langCode] = allLocaleModules[path].default

      languages.push(langCode)
    }
  }

  for (const langCode of languages) {
    let matchedBrandKey

    if (brand) {
      matchedBrandKey = Object.keys(allLocaleModules).find(k =>
        k.replace(/\\/g, "/").endsWith(`/${brand}/${langCode}.json`),
      )
    }

    if (brand && matchedBrandKey) {
      messages[langCode] = merge(
        {},
        defaultLocales[langCode],
        allLocaleModules[matchedBrandKey].default,
      )
    }
    else {
      messages[langCode] = defaultLocales[langCode]
    }
  }

  return { messages, languages }
}

function sortLanguages(languages: string[]): string[] {
  return languages.sort((a, b) => {
    const aIsEnglish = a.startsWith("en-")
    const bIsEnglish = b.startsWith("en-")

    if (aIsEnglish === bIsEnglish) {
      return 0
    }

    return aIsEnglish ? -1 : 1
  })
}

export function createI18nInstance(brandOverride?: string) {
  const { messages, languages } = getMessagesAndLanguages(
    brandOverride ?? import.meta.env.VITE_BRAND,
  )

  const availableLanguages = sortLanguages(languages)

  const defaultFormat = {
    short: {
      year: "numeric",
      month: "short",
      day: "numeric",
    },
    long: {
      year: "numeric",
      month: "long",
      day: "numeric",
      weekday: "long",
      hour: "2-digit",
      minute: "2-digit",
    },
    medium: {
      year: "numeric",
      month: "long",
      day: "numeric",
      weekday: "long",
    },
  } as const

  const datetimeFormats: Record<string, typeof defaultFormat> = {}

  for (const lang of availableLanguages) {
    datetimeFormats[lang] = defaultFormat
  }

  const pluralRules: Record<string, (n: number) => number> = {}

  for (const lang of availableLanguages) {
    if (languageMetadata[lang]) {
      pluralRules[lang] = languageMetadata[lang].pluralize
    }
  }

  const { language: browserLanguage } = useNavigatorLanguage()

  const savedLanguage = (
    typeof localStorage !== "undefined"
      ? localStorage.getItem("currentLanguage")
      : undefined
  ) as (typeof availableLanguages)[number]

  return createI18n<
    Partial<typeof enUS>,
    (typeof availableLanguages)[number],
    false
  >({
    locale: getDefaultLanguage(
      browserLanguage.value,
      savedLanguage,
      availableLanguages,
    ),
    fallbackLocale: "en-US",
    legacy: false,
    globalInjection: false,
    datetimeFormats,
    messages,
    pluralRules,
  })
}

const { languages: _langsForExport } = getMessagesAndLanguages(
  import.meta.env.VITE_BRAND,
)

export const availableLanguages = sortLanguages(_langsForExport)

export const i18n = createI18nInstance()

export type MessageSchema = typeof enUS

export type LanguageOptions = Parameters<
  typeof createI18nInstance
>[0] extends string
  ? string
  : never
